import React from "react";
import { DialogButton, DialogConfig, createDialogConfig } from "common/component/SequentialDialog";
import { formatDateStr, formatDateWithValidate } from "common/util/date";
import {
  AuthorizationInfo,
  ClientReportInfo,
  ClientReportReportStatus,
  ClientReportReportStatusMap,
  ClientReportReportType,
  ClientReportReportTypeMap,
  isAddendumReportFamily,
  isAddendumReportType,
} from "../../domain";
import { createButton, getArticle, getReportTypeDisplayName } from "./utils";

const APPROVED_STATUSES: ClientReportReportStatus[] = [
  ClientReportReportStatus.REPORT_AUTHORIZED,
  ClientReportReportStatus.SUBMITTED_TO_FUNDER,
  ClientReportReportStatus.AUTHORIZATION_RECEIVED,
  ClientReportReportStatus.REPORT_APPROVED_BY_FUNDER,
];

const isAddendumOrChangeHours = (reportType?: string): boolean => {
  if (!reportType) return false;
  const mapped = ClientReportReportTypeMap.get(reportType);
  return !!isAddendumReportFamily(reportType)
    || isAddendumReportType(reportType)
    || mapped === ClientReportReportType.ABA_SHORT_ADDENDUM
    || mapped === ClientReportReportType.CHANGE_HOURS_ADDENDUM;
};

const isSameReportType = (left: string, right: string): boolean => {
  const mappedLeft = ClientReportReportTypeMap.get(left) ?? left;
  const mappedRight = ClientReportReportTypeMap.get(right) ?? right;
  return mappedLeft === mappedRight;
};

const isApproved = (report: ClientReportInfo): boolean => {
  const status = ClientReportReportStatusMap.get(report.reportStatus);
  return APPROVED_STATUSES.includes(status as ClientReportReportStatus);
};

export const findApprovedAddendumConflict = (
  auth: AuthorizationInfo | undefined,
  selectedReportType: string
): ClientReportInfo | undefined => {
  if (!auth || !isAddendumOrChangeHours(selectedReportType)) return undefined;

  return auth.latestSubmittedClientReportByReportType
    ?.filter(report => isAddendumOrChangeHours(report.reportType))
    .filter(report => !isSameReportType(report.reportType, selectedReportType))
    .filter(isApproved)
    .sort((left, right) => (right.authorizedDate ?? "").localeCompare(left.authorizedDate ?? ""))[0];
};

const createContent = (
  auth: AuthorizationInfo | undefined,
  selectedReportType: string
): React.ReactNode => {
  const existingReport = findApprovedAddendumConflict(auth, selectedReportType);
  if (!existingReport) return null;

  const selectedDisplay = getReportTypeDisplayName(selectedReportType);
  const existingDisplay = getReportTypeDisplayName(existingReport.reportType);
  const article = getArticle(existingDisplay);
  const submittedDate = existingReport.submittedAt ? formatDateWithValidate(new Date(existingReport.submittedAt)) : "N/A";
  const approvedDate = existingReport.authorizedDate ? formatDateStr(existingReport.authorizedDate) : "N/A";

  return (
    <div>
      {article} {existingDisplay} was submitted on {submittedDate} and approved on {approvedDate}. Another {selectedDisplay} cannot be submitted from this authorization, unless the Report Status is "Funder Revision Requested".
    </div>
  );
};

const createButtons = (
  auth: AuthorizationInfo | undefined,
  selectedReportType: string
): DialogButton[] => {
  if (!findApprovedAddendumConflict(auth, selectedReportType)) return [];
  return [createButton("OK", { action: "cancel" }, "green", "120px")];
};

export const createApprovedAddendumConflictDialogConfig = (
  auth: AuthorizationInfo | undefined,
  selectedReportType: string,
  shouldShow: boolean
): DialogConfig => {
  return createDialogConfig(
    "approved-addendum-conflict",
    "Confirm!",
    createContent(auth, selectedReportType),
    createButtons(auth, selectedReportType),
    shouldShow,
    false
  );
};
